import { gql } from 'apollo-boost';
import { useApolloClient } from '@apollo/client';

const STARRED_PATCH_FRAGMENT = gql`
    fragment StarredPatchMessage on Message {
        id
        isStarred
        isCheckbox
        isChecked
        folder {
            id
            title
            owner
            parent
            deletedAt
            __typename
        }
    }
`;

const isRefForMessage = (readField, ref, id) => readField('id', ref) === id;

function useUpdateStarred() {
    const client = useApolloClient();

    const patchMessageEntity = message => {
        if (!message?.id) {
            return;
        }

        try {
            const cacheId = client.cache.identify({
                __typename: 'Message',
                id: message.id
            });

            if (!cacheId) {
                return;
            }

            client.cache.writeFragment({
                id: cacheId,
                fragment: STARRED_PATCH_FRAGMENT,
                data: {
                    __typename: 'Message',
                    id: message.id,
                    isStarred: message.isStarred,
                    isCheckbox: message.isCheckbox,
                    isChecked: message.isChecked,
                    folder: message.folder
                        ? {
                              ...message.folder,
                              __typename: message.folder.__typename || 'Folder'
                          }
                        : null
                }
            });
        } catch (error) {
            // Entity might not be normalized yet; ignore and continue with list update.
        }
    };

    const addToStarredGlobal = message => {
        if (!message?.id || !message?.folder?.id) {
            return;
        }

        patchMessageEntity(message);

        client.cache.modify({
            id: 'ROOT_QUERY',
            fields: {
                getStarredMessagesGlobal(existingFolders = [], { readField, toReference }) {
                    const folderId = message.folder.id;
                    const messageRef = toReference({
                        __typename: 'Message',
                        id: message.id
                    });
                    const folderRef =
                        toReference({
                            __typename: 'Folder',
                            id: folderId
                        }) || {
                            __typename: message.folder.__typename || 'Folder',
                            ...message.folder
                        };

                    let foundFolder = false;
                    const updatedFolders = existingFolders.map(entry => {
                        const entryFolder = readField('folder', entry);
                        const entryFolderId = entryFolder ? readField('id', entryFolder) : undefined;
                        if (entryFolderId !== folderId) {
                            return entry;
                        }

                        foundFolder = true;
                        const currentMessages = readField('messages', entry) || [];
                        const alreadyExists = currentMessages.some(ref =>
                            isRefForMessage(readField, ref, message.id)
                        );

                        if (alreadyExists) {
                            return entry;
                        }

                        return {
                            __typename: readField('__typename', entry) || 'FolderContentGlobal',
                            folder: entryFolder || folderRef,
                            messages: [messageRef, ...currentMessages]
                        };
                    });

                    if (foundFolder) {
                        return updatedFolders;
                    }

                    return [
                        {
                            __typename: 'FolderContentGlobal',
                            folder: folderRef,
                            messages: [messageRef]
                        },
                        ...updatedFolders
                    ];
                }
            }
        });
    };

    const removeFromStarredGlobal = message => {
        if (!message?.id || !message?.folder?.id) {
            return;
        }

        patchMessageEntity(message);

        client.cache.modify({
            id: 'ROOT_QUERY',
            fields: {
                getStarredMessagesGlobal(existingFolders = [], { readField }) {
                    const folderId = message.folder.id;
                    const nextFolders = [];

                    existingFolders.forEach(entry => {
                        const entryFolder = readField('folder', entry);
                        const entryFolderId = entryFolder ? readField('id', entryFolder) : undefined;

                        if (entryFolderId !== folderId) {
                            nextFolders.push(entry);
                            return;
                        }

                        const currentMessages = readField('messages', entry) || [];
                        const filteredMessages = currentMessages.filter(
                            ref => !isRefForMessage(readField, ref, message.id)
                        );

                        if (filteredMessages.length > 0) {
                            nextFolders.push({
                                __typename: readField('__typename', entry) || 'FolderContentGlobal',
                                folder: entryFolder,
                                messages: filteredMessages
                            });
                        }
                    });

                    return nextFolders;
                }
            }
        });
    };

    const updateStarred = messages => {
        try {
            messages.forEach(addToStarredGlobal);
        } catch (error) {
            console.log('Error while updating starred messages', error);
        }
    };

    const spliceStarred = messages => {
        try {
            messages.forEach(removeFromStarredGlobal);
        } catch (error) {
            console.log('Error while removing starred messages', error);
        }
    };

    return { updateStarred, spliceStarred };
}

export default useUpdateStarred;
